import { useCallback, useEffect, useRef, useState } from 'react';
import apiService from '../services/apiService';

export const ACTIVITIES_ERROR = 'Tasks for this unit could not be loaded.';

export const isOpenActivity = (a) => a.status === 'open' || a.status === 'in_progress';

/**
 * The tasks (activities) of one unit: GET /api/activities/unit/{stock} on
 * open and per unit (P-UNIT-116), with today's list versioning: every read and
 * every local write takes a ticket, and a read only lands when nothing newer
 * happened since it was sent, so a slow or stale GET can never overwrite a
 * change the user has just saved.
 *
 * Shared by today's Activity tab and the full unit page, where the hero's
 * "N open tasks" chip and the tab count read the same list as the tab (one
 * request, not two). `enabled: false` makes it inert (the caller was given
 * the list by its parent).
 */
export default function useUnitActivities(stockNumber, { enabled = true } = {}) {
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loaded, setLoaded] = useState(false);
    // A failed read (e.g. 503) is an error, not "no tasks" (QA P-2).
    const [error, setError] = useState(null);
    const listVersion = useRef(0);

    const fetchActivities = useCallback(({ silent = false } = {}) => {
        const ticket = ++listVersion.current;
        if (!silent) setLoading(true);
        return apiService.getUnitActivities(stockNumber)
            .then((data) => { if (ticket === listVersion.current) { setActivities(data.activities || []); setLoaded(true); setError(null); } })
            .catch(() => { if (ticket === listVersion.current) { setActivities([]); setLoaded(false); setError(ACTIVITIES_ERROR); } })
            .finally(() => { if (ticket === listVersion.current) setLoading(false); });
    }, [stockNumber]);

    useEffect(() => {
        if (!enabled) return;
        setActivities([]);
        setLoaded(false);
        setError(null);
        fetchActivities();
    }, [fetchActivities, enabled]);

    // The update endpoints answer with the stored document, so the list is
    // patched from that instead of re-reading it. Without a document, the
    // sent fields are applied.
    const applyUpdate = useCallback((activityId, saved, sent) => {
        listVersion.current += 1;
        setLoading(false);
        const fromServer = saved && saved.id === activityId ? saved : null;
        setActivities((prev) => prev.map((a) => (a.id === activityId ? { ...a, ...(fromServer || sent) } : a)));
    }, []);

    const removeLocal = useCallback((activityId) => {
        listVersion.current += 1;
        setActivities((prev) => prev.filter((a) => a.id !== activityId));
    }, []);

    // Put a removed task back (Undo), at its old position when known.
    const restoreLocal = useCallback((activity, index = 0) => {
        listVersion.current += 1;
        setActivities((prev) => {
            if (prev.some((a) => a.id === activity.id)) return prev;
            const next = [...prev];
            next.splice(Math.min(Math.max(index, 0), next.length), 0, activity);
            return next;
        });
    }, []);

    const openCount = activities.filter(isOpenActivity).length;

    return {
        activities,
        setActivities,
        loading,
        loaded,
        error,
        openCount,
        closedCount: activities.length - openCount,
        fetchActivities,
        applyUpdate,
        removeLocal,
        restoreLocal,
        listVersion,
    };
}
